import { zodResolver } from "@hookform/resolvers/zod";
import {
  getLocalTimeZone,
  now,
  toCalendarDateTime,
} from "@internationalized/date";
import { TimedDuration } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/infrastructureEventModal/durationInputs/TimedDuration";
import {
  useAddVehicle,
  useGetIncidentNotes,
  useGetOperators,
  useGetTrainStations,
  useUpdateVehicle,
} from "features/VehicleSidebar/VehicleList/AddVehicles/useInfoDataForVehicles";
import { useEffect, useMemo, useRef, useState } from "react";
import { Controller, FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ActionModal } from "shared/components/ActionModal";
import {
  Alert,
  Autocomplete,
  AutocompleteItem,
  Field,
  Flex,
  Input,
  Radio,
  RadioGroup,
  Text,
  VStack,
  tokens,
} from "shared/components/Spor";
import { useFormVerbose } from "shared/form/useFormVerbose";
import {
  AddReserveAlternativeTransportRequest,
  AddReserveAlternativeTransportRequestSchema,
  ReserveAlternativeTransport,
  vehicleTypeSchema,
} from "shared/types/alternativeTransport";
import { toFormTime } from "shared/utils/dateTimeUtils";
import styled from "styled-components";

const RadioGroupStyled = styled(RadioGroup)`
  display: flex;
  justify-content: flex-start;
  gap: ${tokens.default.size.spacing[2]};
`;

const Section = styled(Flex)`
  flex-direction: column;
  margin-top: ${tokens.default.size.spacing[4]};
  gap: ${tokens.default.size.spacing[1.5]};
  width: 100%;
`;

/**
 * Keeps the scrollable area contained within the reservebuss modal and ensures the modal is scrolled to bottom so the list isn't tiny.
 */
const useFitComboboxToFooter = (wrapperRef: {
  current: HTMLDivElement | null;
}) => {
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    // Nearest scrollable ancestor (the modal's scrollable body).
    const getScrollParent = (el: HTMLElement | null): HTMLElement | null => {
      let node = el?.parentElement ?? null;
      while (node) {
        const { overflowY } = getComputedStyle(node);
        if (
          (overflowY === "auto" || overflowY === "scroll") &&
          node.scrollHeight > node.clientHeight
        ) {
          return node;
        }
        node = node.parentElement;
      }
      return null;
    };

    // Scroll the modal body to the bottom when the input gains focus
    const scrollToBottomOnFocus = () => {
      if (wrapper.querySelector('ul[role="listbox"]')) return;
      getScrollParent(wrapper)?.scrollTo({ top: Number.MAX_SAFE_INTEGER });
    };

    const adjust = () => {
      const listbox = wrapper.querySelector<HTMLElement>('ul[role="listbox"]');
      if (!listbox) {
        return;
      }

      // Cap the height so the list ends above the footer.
      const dialogContent = wrapper.closest<HTMLElement>(
        '[data-scope="dialog"][data-part="content"]',
      );
      const submitButton = dialogContent?.querySelector<HTMLElement>(
        '[data-testid="modal_submitButton"]',
      );
      let footer: HTMLElement | null = submitButton ?? null;
      while (footer && footer.parentElement !== dialogContent) {
        footer = footer.parentElement;
      }
      const boundaryTop = footer
        ? footer.getBoundingClientRect().top
        : window.innerHeight;

      const available = boundaryTop - listbox.getBoundingClientRect().top - 8;
      if (available > 0) {
        listbox.style.maxHeight = `${available}px`;
      }
    };

    const observer = new MutationObserver(adjust);
    observer.observe(wrapper, { childList: true, subtree: true });
    wrapper.addEventListener("focusin", scrollToBottomOnFocus);
    window.addEventListener("resize", adjust);
    window.addEventListener("scroll", adjust, true);
    adjust();

    return () => {
      observer.disconnect();
      wrapper.removeEventListener("focusin", scrollToBottomOnFocus);
      window.removeEventListener("resize", adjust);
      window.removeEventListener("scroll", adjust, true);
    };
  }, [wrapperRef]);
};

type AddVehicleProps = {
  closeModal: () => void;
  isReserveBusTabSelected: boolean;
  editVehicle?: ReserveAlternativeTransport;
};

const AddVehicle = ({
  closeModal,
  isReserveBusTabSelected,
  editVehicle,
}: AddVehicleProps) => {
  const { t } = useTranslation();
  const isEdit = !!editVehicle;
  const [searchQuery, setSearchQuery] = useState("");
  const [operatorSearchQuery, setOperatorSearchQuery] = useState(
    editVehicle?.operatorName ?? "",
  );
  const [isirkSearchQuery, setIsirkSearchQuery] = useState(
    editVehicle?.isirkId ?? "",
  );

  const currentTime = now(getLocalTimeZone());
  const nowTime = toCalendarDateTime(currentTime);
  const nowTimePlusFourHours = toCalendarDateTime(currentTime).add({
    hours: 4,
  });

  const formMethods = useFormVerbose<AddReserveAlternativeTransportRequest>({
    resolver: zodResolver(AddReserveAlternativeTransportRequestSchema),
    defaultValues: {
      serviceType:
        editVehicle?.serviceType ??
        (isReserveBusTabSelected ? "RESERVE" : "BACKUP"),
      operatorName: editVehicle?.operatorName ?? "",
      operatorId: editVehicle?.operatorId ?? null,
      shiftStartTime: toFormTime(editVehicle?.shiftStartTime) ?? nowTime,
      shiftEndTime:
        toFormTime(editVehicle?.shiftEndTime) ?? nowTimePlusFourHours,
      iSirkId: editVehicle?.isirkId ?? null,
      referenceNumber: editVehicle?.referenceNumber ?? null,
      driverPhoneNumber: editVehicle?.driverPhoneNumber ?? "",
      vehicleType:
        editVehicle?.type === "BUS" || editVehicle?.type === "MINIBUS"
          ? editVehicle.type
          : "BUS",
      baseStopReference: editVehicle?.baseStopReference ?? undefined,
      stopName: editVehicle?.baseStopName ?? "",
    },
  });

  const {
    formState: { errors },
    reset: resetForm,
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
  } = formMethods;

  const serviceType = watch("serviceType");
  const isReserve = serviceType === "RESERVE";
  const isFromPlan = isEdit && !!editVehicle?.workShiftId;
  const comboboxWrapperRef = useRef<HTMLDivElement>(null);
  useFitComboboxToFooter(comboboxWrapperRef);

  const [isManualOperator, setIsManualOperator] = useState(
    isEdit ? !editVehicle?.operatorId : false,
  );
  const operatorId = watch("operatorId");
  const [date] = useState(new Date());
  const jbvId = editVehicle?.baseStopReference?.jbvId;

  const {
    data: operationalPointResponse,
    isSuccess: isOpPointResponseSuccess,
    isError: isOpPointResponseError,
  } = useGetTrainStations();

  const {
    mutate: postAddVehicle,
    status: postAddVehicleStatus,
    reset: resetStatus,
  } = useAddVehicle();

  const {
    mutate: postUpdateVehicle,
    status: updateVehicleStatus,
    reset: resetUpdateStatus,
  } = useUpdateVehicle(editVehicle?.id ?? "");

  const mutationStatus = isEdit ? updateVehicleStatus : postAddVehicleStatus;

  const { data: notes, isError: isNotesError } = useGetIncidentNotes(date);

  const { data: operators } = useGetOperators();
  const operatorsList = operators ?? [];

  const currentISirkId = watch("iSirkId");

  const incidentNoteOptions = useMemo<
    { identifier: string; isirk: string }[]
  >(() => {
    const options = (notes?.incidentNotes ?? []).map((note) => ({
      identifier: note.identifier,
      isirk: note.isirk,
    }));

    if (
      currentISirkId &&
      !options.some((option) => option.isirk === currentISirkId)
    ) {
      return [
        { identifier: currentISirkId, isirk: currentISirkId },
        ...options,
      ];
    }

    return options;
  }, [notes, currentISirkId]);

  const onSubmitForm = handleSubmit((data) =>
    isEdit ? postUpdateVehicle(data) : postAddVehicle(data),
  );

  const onClose = () => {
    closeModal();
    resetForm();
    resetStatus();
    resetUpdateStatus();
  };

  const filteredStations = useMemo(() => {
    if (!isOpPointResponseSuccess || !operationalPointResponse?.railStations)
      return [];

    const query = searchQuery.toLowerCase().trim();
    if (!query) return [];

    return operationalPointResponse.railStations
      .filter((station) => station.name.toLowerCase().includes(query))
      .sort((a, b) => {
        const aName = a.name.toLowerCase();
        const bName = b.name.toLowerCase();
        const aStarts = aName.startsWith(query);
        const bStarts = bName.startsWith(query);

        // Prefix matches first, then alphabetical.
        if (aStarts !== bStarts) return aStarts ? -1 : 1;
        return aName.localeCompare(bName, "nb");
      });
  }, [searchQuery, operationalPointResponse, isOpPointResponseSuccess]);

  const onSearchSelectionChange = (selectedStation: string) => {
    const selected = filteredStations.find((s) => s.name === selectedStation);
    if (!selected) return;

    setValue(
      "baseStopReference",
      {
        nsrCode:
          selected.externalIds.find((e) => e.sourceSystem === "NSR")?.id ??
          null,
        jbvId:
          selected.externalIds.find((e) => e.sourceSystem === "TRAINPLAN")
            ?.id ?? null,
        uicCode: null,
        hastusCode: null,
        nsrStopPlace: null,
        nsrQuay: null,
      },
      { shouldValidate: true },
    );
  };

  useEffect(() => {
    if (!jbvId || !operationalPointResponse) return;

    const matchedStation = operationalPointResponse?.railStations.find(
      (station) =>
        station.externalIds.some(
          (e) => e.sourceSystem === "TRAINPLAN" && e.id === jbvId,
        ),
    );

    if (matchedStation) {
      setSearchQuery(matchedStation.name);
    }
  }, [jbvId, operationalPointResponse]);

  const addSuccessMessage = isReserve
    ? "Reserve er lagt til"
    : "Beredskap er lagt til";

  return (
    <FormProvider {...formMethods}>
      <ActionModal
        title={
          isEdit
            ? `Rediger ${isReserve ? "reservebuss" : "beredskapsbuss"}`
            : `Legg til ${isReserve ? "reservebuss" : "beredskapsbuss"}`
        }
        actionTitle={
          isEdit
            ? "Lagre endringer"
            : `Legg til ${isReserve ? "reserve" : "beredskap"}`
        }
        onClose={onClose}
        onSubmit={onSubmitForm}
        successMessage={isEdit ? "Endringer lagret" : addSuccessMessage}
        failureMessage={isEdit ? "Endring feilet" : "Oppretting feilet"}
        loading={mutationStatus === "pending"}
        isSuccess={mutationStatus === "success"}
        isError={mutationStatus === "error"}
      >
        {(isOpPointResponseError || isNotesError) && (
          <Alert
            data-color="critical"
            title="Feil ved henting av nødvendig data"
            style={{ width: "100%" }}
          >
            Det oppstod en feil ved henting av nødvendig data. Det er ikke mulig
            å sette opp busser akkurat nå. Vennligst prøv igjen senere.
          </Alert>
        )}

        <VStack gap="0" alignItems="start" width="100%">
          <Flex direction="column" gap="1.5" width="100%">
            <Text fontWeight="700">Type tjeneste</Text>
            <Alert data-color="info" width="100%">
              {isReserve
                ? "Reservebuss er også synlig for kundeveiledere i Brudd."
                : "Beredskapsbuss er kun synlig for DROPS."}
            </Alert>
            <Controller
              name="serviceType"
              control={formMethods.control}
              render={({ field }) => (
                <RadioGroupStyled
                  value={field.value}
                  onValueChange={(details) => {
                    field.onChange(details.value);
                    // I-Sirk is reserve only, clear it when switching to beredskap so no stale I-Sirk link is submitted
                    if (details.value !== "RESERVE") {
                      setValue("iSirkId", null);
                      setIsirkSearchQuery("");
                    }
                  }}
                >
                  <Radio value="RESERVE">Reserve</Radio>
                  <Radio value="BACKUP">Beredskap</Radio>
                </RadioGroupStyled>
              )}
            />
          </Flex>
          <Section>
            <Text fontWeight="700">Type kjøretøy</Text>
            <Controller
              name="vehicleType"
              control={formMethods.control}
              render={({ field }) => (
                <RadioGroupStyled
                  value={field.value}
                  onValueChange={(details) => {
                    field.onChange(details.value);
                  }}
                >
                  <Radio value={vehicleTypeSchema.enum.BUS}>Buss</Radio>
                  <Radio value={vehicleTypeSchema.enum.MINIBUS}>Minibuss</Radio>
                </RadioGroupStyled>
              )}
            />
          </Section>
          <Section>
            <Text fontWeight="700">Tilgjengelig</Text>
            <TimedDuration
              fromTimeField="shiftStartTime"
              toTimeField="shiftEndTime"
            />
          </Section>
          <Section>
            <Text fontWeight="700">Leverandør</Text>
            <RadioGroupStyled
              value={isManualOperator ? "manual" : "list"}
              onValueChange={(details) => {
                const manual = details.value === "manual";
                setIsManualOperator(manual);
                setValue("operatorId", null);
                setOperatorSearchQuery("");
                if (!manual) setValue("operatorName", "");
              }}
            >
              <Radio value="list">Velg fra liste</Radio>
              <Radio value="manual">Skriv navn manuelt</Radio>
            </RadioGroupStyled>
            <Field
              invalid={!!errors.operatorName}
              errorText={
                isManualOperator ? "Selskap må fylles ut" : "Selskap må velges"
              }
              width="100%"
              aria-invalid={!!errors.operatorName}
              aria-describedby={
                errors.operatorName ? "operatorName-error" : undefined
              }
            >
              {isManualOperator ? (
                <Input
                  label="Leverandør"
                  required
                  {...register("operatorName", {
                    onChange: () =>
                      setValue("operatorName", getValues("operatorName"), {
                        shouldValidate: true,
                      }),
                  })}
                />
              ) : (
                <Autocomplete
                  label="Leverandør"
                  name="operatorId"
                  data-testid="operatorSelect"
                  filteredExternally
                  required
                  inputValue={operatorSearchQuery}
                  onInputValueChange={({ inputValue }) =>
                    setOperatorSearchQuery(inputValue)
                  }
                  value={operatorId ? [operatorId] : []}
                  onValueChange={({ value }) => {
                    const selected = operatorsList.find(
                      (operator) => operator.id.toString() === value[0],
                    );
                    setValue("operatorName", selected?.name ?? "", {
                      shouldValidate: true,
                    });
                    setValue("operatorId", selected?.id.toString() ?? null);
                  }}
                >
                  {operatorsList
                    .filter((operator) =>
                      operator.name
                        .toLowerCase()
                        .includes(operatorSearchQuery.toLowerCase().trim()),
                    )
                    .map((operator) => (
                      <AutocompleteItem
                        key={operator.id}
                        item={{
                          label: operator.name,
                          value: operator.id.toString(),
                        }}
                      >
                        {operator.name}
                      </AutocompleteItem>
                    ))}
                </Autocomplete>
              )}
            </Field>
            <Field
              invalid={!!errors.driverPhoneNumber}
              errorText="Telefonnummer må fylles ut"
              width="100%"
              aria-invalid={!!errors.driverPhoneNumber}
              aria-describedby={
                errors.driverPhoneNumber ? "driverPhoneNumber-error" : undefined
              }
            >
              <Input
                required
                label="Telefonnummer"
                {...register("driverPhoneNumber")}
              />
            </Field>
          </Section>

          <Section>
            <Text fontWeight="700">Lokasjon</Text>
            <Field
              invalid={!!errors.baseStopReference}
              errorText="Stasjon må velges"
              width="100%"
              aria-invalid={!!errors.baseStopReference}
              aria-describedby={
                errors.baseStopReference ? "baseStopReference-error" : undefined
              }
            >
              <Autocomplete
                label="Stasjon"
                filteredExternally
                required
                onInputValueChange={({ inputValue }) =>
                  setSearchQuery(inputValue)
                }
                inputValue={searchQuery}
                onValueChange={({ value }) => onSearchSelectionChange(value[0])}
              >
                {filteredStations.map((station) => (
                  <AutocompleteItem
                    key={station.name}
                    item={{ label: station.name, value: station.name }}
                  >
                    {station.name}
                  </AutocompleteItem>
                ))}
              </Autocomplete>
            </Field>
            <Field
              invalid={!!errors.stopName}
              errorText="Stoppnavn må oppgis"
              width="100%"
              aria-invalid={!!errors.stopName}
              aria-describedby={errors.stopName ? "stopName-error" : undefined}
            >
              <Input
                required
                label="Lokasjon"
                {...register("stopName", {
                  onChange: () =>
                    setValue("stopName", getValues("stopName"), {
                      shouldValidate: true,
                    }),
                })}
              />
            </Field>
          </Section>

          <Section>
            <Text fontWeight="700">Referanser (valgfritt)</Text>
            {isFromPlan && (
              <Alert data-color="info" style={{ width: "100%" }}>
                I-Sirk kan ikke endres når du redigerer et kjøretøy fra plan.
              </Alert>
            )}

            <Field width="100%">
              <Autocomplete
                label="I-sirk "
                name="iSirkId"
                filteredExternally
                placeholder={
                  incidentNoteOptions.length === 0
                    ? t("Ingen i-isirk tilgjengelig denne datoen")
                    : t("Velg i-isirk")
                }
                inputValue={isirkSearchQuery}
                onInputValueChange={({ inputValue }) =>
                  setIsirkSearchQuery(inputValue)
                }
                value={currentISirkId ? [currentISirkId] : []}
                onValueChange={({ value }) =>
                  setValue("iSirkId", value[0] ?? null, {
                    shouldValidate: true,
                    shouldDirty: true,
                  })
                }
                disabled={isFromPlan}
              >
                {incidentNoteOptions
                  .filter((note) =>
                    note.isirk
                      .toLowerCase()
                      .includes(isirkSearchQuery.toLowerCase().trim()),
                  )
                  .map((note) => (
                    <AutocompleteItem
                      key={note.identifier}
                      item={{ label: note.isirk, value: note.isirk }}
                    >
                      {note.isirk}
                    </AutocompleteItem>
                  ))}
              </Autocomplete>
            </Field>
            <Input label="Referansenummer " {...register("referenceNumber")} />
          </Section>
        </VStack>
      </ActionModal>
    </FormProvider>
  );
};

export default AddVehicle;
